import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import BalanceRounded from '@mui/icons-material/BalanceRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import MoneyOffRounded from '@mui/icons-material/MoneyOffRounded';
import SavingsOutlined from '@mui/icons-material/SavingsOutlined';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import {
  Box,
  Card,
  Chip,
  FormControlLabel,
  Link,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Fragment, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { api, unwrapJson } from '../../../api/client';
import { EmptyState } from '../../../components/states';
import { humanize } from '../../../components/StatusChip';
import { businessDate, moneyCompact } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { ShareBar } from '../components/charts';
import { AsOfControl } from '../components/DateRangeControl';
import { ReportFrame } from '../components/ReportFrame';
import { ReportTable } from '../components/ReportTable';
import { linkTo } from '../components/useEntityLink';
import { Panel, QueryError, StatGrid, StatTile } from '../components/ui';
import { useReportParams, usePeriodText, type ReportParams } from '../components/useReportParams';
import { fetchCsv, reportFileName } from '../lib/csv';
import { isIsoDate } from '../lib/dateRange';
import { num } from '../lib/numbers';
import { ChartRow, M, RangeControls } from './common';

interface StatementLine {
  accountId: string;
  code: string;
  name: string;
  amount: string;
}

export interface StatementSection {
  title: string;
  lines: StatementLine[];
  total: string | number;
  totalLabel: string;
}

function AccountName({ id, code, name }: { id: string; code: string; name: string }) {
  const to = code ? linkTo('account', id) : null;
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', minWidth: 0 }}>
      <Typography
        variant="caption"
        className="tabular"
        color="text.secondary"
        sx={{ minWidth: 44, flexShrink: 0 }}
      >
        {code}
      </Typography>
      {to ? (
        <Link component={RouterLink} to={to} underline="hover" color="inherit">
          {name}
        </Link>
      ) : (
        <span>{name}</span>
      )}
    </Box>
  );
}

/** A financial statement: titled sections of account lines with subtotals, then summary lines. */
export function Statement({
  sections,
  summary,
  loading,
}: {
  sections: StatementSection[];
  summary: { label: string; value: string | number; emphasis?: boolean }[];
  loading?: boolean;
}) {
  if (loading)
    return (
      <Card sx={{ p: 2.5 }}>
        {Array.from({ length: 10 }, (_, i) => (
          <Skeleton key={i} height={34} width={i % 4 === 0 ? '40%' : '100%'} />
        ))}
      </Card>
    );
  return (
    <Card>
      <Table size="small" sx={{ '& td': { py: 1.1 } }}>
        <TableBody>
          {sections.map((s) => (
            <Fragment key={s.title}>
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{
                    pt: 2.5,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  {s.title}
                </TableCell>
              </TableRow>
              {s.lines.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} sx={{ color: 'text.disabled', pl: 3 }}>
                    Nothing posted
                  </TableCell>
                </TableRow>
              )}
              {s.lines.map((l) => (
                <TableRow key={l.accountId} hover>
                  <TableCell sx={{ pl: 3 }}>
                    <AccountName id={l.accountId} code={l.code} name={l.name} />
                  </TableCell>
                  <TableCell align="right">
                    <M v={l.amount} signed />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 650 }}>{s.totalLabel}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 650 }}>
                  <M v={s.total} signed />
                </TableCell>
              </TableRow>
            </Fragment>
          ))}
          {summary.map((r) => (
            <TableRow key={r.label} sx={{ bgcolor: r.emphasis ? 'action.hover' : undefined }}>
              <TableCell
                sx={{
                  fontWeight: r.emphasis ? 800 : 650,
                  fontSize: r.emphasis ? '1rem' : undefined,
                  borderTop: 2,
                  borderColor: 'divider',
                  borderBottom: r.emphasis ? 0 : undefined,
                }}
              >
                {r.label}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: r.emphasis ? 800 : 650,
                  fontSize: r.emphasis ? '1rem' : undefined,
                  borderTop: 2,
                  borderColor: 'divider',
                  borderBottom: r.emphasis ? 0 : undefined,
                }}
              >
                <M v={r.value} signed />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function BalancedChip({ balanced }: { balanced: boolean | undefined }) {
  if (balanced === undefined) return null;
  return (
    <Chip
      icon={balanced ? <CheckCircleRounded /> : <ErrorOutlineRounded />}
      color={balanced ? 'success' : 'error'}
      variant="outlined"
      label={balanced ? 'Balanced' : 'Not balanced'}
      sx={{ fontWeight: 650, bgcolor: 'background.paper' }}
    />
  );
}

function useAsOf(p: ReportParams): string {
  const v = p.get('asOf');
  return isIsoDate(v) ? v : p.today;
}

export function ProfitAndLossReport() {
  const p = useReportParams('thisMonth');
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['reports', 'profit-and-loss', p.range],
    queryFn: () => unwrapJson(api.GET('/reports/profit-and-loss', { params: { query: p.range } })),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const netProfit = num(d?.netProfit);
  const totalIncome = num(d?.totalIncome);
  return (
    <ReportFrame
      slug="profit-and-loss"
      period={usePeriodText(p)}
      controls={<RangeControls p={p} />}
      onExport={() =>
        fetchCsv(
          api.GET('/reports/profit-and-loss', {
            params: { query: { ...p.range, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('profit-and-loss', p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Income"
          value={d && moneyCompact(d.totalIncome, format)}
          icon={<TrendingUpRounded />}
          loading={q.isLoading}
        />
        <StatTile
          label="Gross profit"
          value={d && moneyCompact(d.grossProfit, format)}
          hint={d && `Cost of sales ${moneyCompact(d.costOfSales, format)}`}
          icon={<SavingsOutlined />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label="Expenses"
          value={d && moneyCompact(d.totalExpenses, format)}
          icon={<MoneyOffRounded />}
          tone="warning"
          loading={q.isLoading}
        />
        <StatTile
          label={netProfit < 0 ? 'Net loss' : 'Net profit'}
          value={d && moneyCompact(d.netProfit, format)}
          hint={
            totalIncome ? `${((netProfit / totalIncome) * 100).toFixed(1)}% of income` : undefined
          }
          icon={<AccountBalanceOutlined />}
          tone={netProfit < 0 ? 'error' : 'success'}
          loading={q.isLoading}
        />
      </StatGrid>
      <Statement
        loading={q.isLoading}
        sections={[
          {
            title: 'Income',
            lines: d?.income ?? [],
            total: d?.totalIncome ?? 0,
            totalLabel: 'Total income',
          },
          {
            title: 'Expenses',
            lines: d?.expenses ?? [],
            total: d?.totalExpenses ?? 0,
            totalLabel: 'Total expenses',
          },
        ]}
        summary={[
          { label: 'Gross profit (income − cost of sales)', value: d?.grossProfit ?? 0 },
          {
            label: netProfit < 0 ? 'Net loss' : 'Net profit',
            value: d?.netProfit ?? 0,
            emphasis: true,
          },
        ]}
      />
    </ReportFrame>
  );
}

export function BalanceSheetReport() {
  const p = useReportParams();
  const { format } = useShop();
  const asOf = useAsOf(p);
  const q = useQuery({
    queryKey: ['reports', 'balance-sheet', asOf],
    queryFn: () => unwrapJson(api.GET('/reports/balance-sheet', { params: { query: { asOf } } })),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  return (
    <ReportFrame
      slug="balance-sheet"
      period={`As of ${businessDate(asOf, format)}`}
      controls={
        <>
          <AsOfControl
            value={asOf}
            today={p.today}
            onChange={(v) => p.update({ asOf: v === p.today ? null : v })}
          />
          <BalancedChip balanced={d?.balanced} />
        </>
      }
      onExport={() =>
        fetchCsv(
          api.GET('/reports/balance-sheet', {
            params: { query: { asOf, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('balance-sheet', asOf),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Assets"
          value={d && moneyCompact(d.totalAssets, format)}
          icon={<AccountBalanceOutlined />}
          loading={q.isLoading}
        />
        <StatTile
          label="Liabilities"
          value={d && moneyCompact(d.totalLiabilities, format)}
          icon={<MoneyOffRounded />}
          tone="warning"
          loading={q.isLoading}
        />
        <StatTile
          label="Equity"
          value={d && moneyCompact(d.totalEquity, format)}
          hint={d && `Includes earnings ${moneyCompact(d.earnings, format)}`}
          icon={<SavingsOutlined />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      {d && (
        <Panel title="What the business owns, and who funds it" sx={{ mb: 2.5 }}>
          <ShareBar
            parts={[
              {
                label: 'Liabilities',
                value: num(d.totalLiabilities),
                color: 'var(--mui-palette-warning-main)',
              },
              {
                label: 'Equity',
                value: num(d.totalEquity),
                color: 'var(--mui-palette-success-main)',
              },
            ]}
          />
        </Panel>
      )}
      <ChartRow ratio="1fr 1fr">
        <Statement
          loading={q.isLoading}
          sections={[
            {
              title: 'Assets',
              lines: d?.assets ?? [],
              total: d?.totalAssets ?? 0,
              totalLabel: 'Total assets',
            },
          ]}
          summary={[]}
        />
        <Statement
          loading={q.isLoading}
          sections={[
            {
              title: 'Liabilities',
              lines: d?.liabilities ?? [],
              total: d?.totalLiabilities ?? 0,
              totalLabel: 'Total liabilities',
            },
            {
              title: 'Equity',
              lines: [
                ...(d?.equity ?? []),
                ...(d && num(d.earnings) !== 0
                  ? [
                      {
                        accountId: 'earnings',
                        code: '',
                        name: 'Retained & current earnings',
                        amount: d.earnings,
                      },
                    ]
                  : []),
              ],
              total: d?.totalEquity ?? 0,
              totalLabel: 'Total equity',
            },
          ]}
          summary={[
            {
              label: 'Liabilities + equity',
              value: d?.totalLiabilitiesAndEquity ?? 0,
              emphasis: true,
            },
          ]}
        />
      </ChartRow>
    </ReportFrame>
  );
}

const TYPE_ORDER = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'] as const;

export function TrialBalanceReport() {
  const p = useReportParams();
  const { format } = useShop();
  const asOf = useAsOf(p);
  const includeZero = p.get('includeZero') === 'true' ? ('true' as const) : undefined;
  const query = { asOf, includeZero };
  const q = useQuery({
    queryKey: ['reports', 'trial-balance', query],
    queryFn: () => unwrapJson(api.GET('/reports/trial-balance', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const rows = d
    ? [...d.data].sort(
        (a, b) =>
          TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type) || a.code.localeCompare(b.code),
      )
    : undefined;
  return (
    <ReportFrame
      slug="trial-balance"
      period={`As of ${businessDate(asOf, format)}`}
      controls={
        <>
          <AsOfControl
            value={asOf}
            today={p.today}
            onChange={(v) => p.update({ asOf: v === p.today ? null : v })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeZero === 'true'}
                onChange={(e) => p.update({ includeZero: e.target.checked ? 'true' : null })}
              />
            }
            label="Show zero balances"
          />
          <BalancedChip balanced={d?.balanced} />
        </>
      }
      onExport={() =>
        fetchCsv(
          api.GET('/reports/trial-balance', {
            params: { query: { ...query, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('trial-balance', asOf),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Total debits"
          value={d && moneyCompact(d.totals.debit, format)}
          icon={<BalanceRounded />}
          loading={q.isLoading}
        />
        <StatTile
          label="Total credits"
          value={d && moneyCompact(d.totals.credit, format)}
          icon={<BalanceRounded />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label="Accounts"
          value={d?.data.length}
          hint={includeZero ? 'Including zero balances' : 'With a balance'}
          icon={<AccountBalanceOutlined />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      <ReportTable
        loading={q.isLoading}
        rows={rows}
        rowKey={(r) => r.accountId}
        dense
        empty={
          <EmptyState
            icon={<BalanceRounded />}
            title="Nothing posted yet"
            body="Account balances appear once sales, purchases or payments are posted."
          />
        }
        columns={[
          {
            key: 'account',
            header: 'Account',
            render: (r) => <AccountName id={r.accountId} code={r.code} name={r.name} />,
          },
          {
            key: 'type',
            header: 'Type',
            hideBelow: 'sm',
            render: (r): ReactNode => (
              <Typography variant="caption" color="text.secondary">
                {humanize(r.type)}
              </Typography>
            ),
          },
          {
            key: 'debit',
            header: 'Debit',
            align: 'right',
            render: (r) => (num(r.debit) ? <M v={r.debit} /> : ''),
            footer: <M v={d?.totals.debit} />,
          },
          {
            key: 'credit',
            header: 'Credit',
            align: 'right',
            render: (r) => (num(r.credit) ? <M v={r.credit} /> : ''),
            footer: <M v={d?.totals.credit} />,
          },
        ]}
      />
    </ReportFrame>
  );
}
